import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Box, Paper, Button, Tooltip, Link, TextField, Chip, } from '@weave-mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Select, MenuItem, FormControl, } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../api/apiClient';
import { queryKeys } from '../../query/queryClient';
import { triggerBrowserDownload } from '../../utils/download';
const STATUS_CONFIG = {
    scheduled: { label: 'Scheduled', color: '#757575' },
    inprogress: { label: 'In Progress', color: '#ed6c02' },
    cancelling: { label: 'Cancelling…', color: '#ed6c02' },
    completed: { label: 'Completed', color: '#2e7d32' },
    failed: { label: 'Failed', color: '#d32f2f' },
    cancelled: { label: 'Cancelled', color: '#424242' },
};
const STATUS_FILTER_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({
    value: value,
    label,
}));
function formatDateTime(isoString) {
    const d = new Date(isoString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}
const JOB_TABLE_COLUMNS = [
    { id: 'batchId', header: 'Batch Id', getValue: (job) => job.batchId },
    { id: 'jobId', header: 'Job Id', getValue: (job) => job.jobId },
    { id: 'task', header: 'Task', getValue: (job) => job.caseName || '—' },
    { id: 'dbSnapshot', header: 'DB Snapshot', getValue: (job) => job.dbSnapshotDate },
    {
        id: 'status',
        header: 'Status',
        getValue: (job) => STATUS_CONFIG[job.status]?.label ?? job.status,
    },
    { id: 'createdBy', header: 'Created By', getValue: (job) => job.createdBy },
    {
        id: 'createdAt',
        header: 'Created At',
        getValue: (job) => formatDateTime(job.createdAt),
    },
    {
        id: 'collections',
        header: 'Collections',
        getValue: (job) => String(job.collectionCount),
    },
    {
        id: 'results',
        header: 'Results',
        getValue: (job) => (job.s3ResultKey ? 'Download Results' : 'Unavailable'),
        minChars: 16,
    },
    {
        id: 'action',
        header: 'Action',
        getValue: (job) => (job.status === 'inprogress' ? 'Cancel' : ''),
        minChars: 18,
    },
];
const EQUAL_COLUMN_WEIGHT = 10;
function computeJobTableColumnWidths(jobs) {
    const weights = JOB_TABLE_COLUMNS.map((col) => {
        const contentMax = jobs.reduce((max, job) => Math.max(max, col.getValue(job).length), 0);
        const minChars = col.minChars ?? 0;
        return EQUAL_COLUMN_WEIGHT + Math.max(col.header.length, contentMax, minChars);
    });
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    return weights.map((weight) => `${((weight / total) * 100).toFixed(2)}%`);
}
function buildJobQueryParams(cursor, filters) {
    const params = {};
    if (cursor)
        params.cursor = cursor;
    if (filters.batchId)
        params.batchId = filters.batchId;
    if (filters.task)
        params.task = filters.task;
    if (filters.dbSnapshotDate)
        params.dbSnapshotDate = filters.dbSnapshotDate;
    if (filters.status)
        params.status = filters.status;
    if (filters.createdBy)
        params.createdBy = filters.createdBy;
    if (filters.createdAt)
        params.createdAt = filters.createdAt;
    return params;
}
const FILTER_FONT_SIZE = '12px';
const FILTER_TOKENS = {
    height: 28,
    radius: 0.75,
    bg: '#ffffff',
    bgActive: 'rgba(6, 150, 215, 0.06)',
    border: 'rgba(0, 0, 0, 0.14)',
    borderHover: 'rgba(0, 0, 0, 0.28)',
    borderActive: '#0696D7',
    placeholder: 'rgba(0, 0, 0, 0.38)',
};
const placeholderColorSx = {
    color: `${FILTER_TOKENS.placeholder} !important`,
    opacity: '1 !important',
    WebkitTextFillColor: `${FILTER_TOKENS.placeholder} !important`,
};
function filterInputProps(ariaLabel) {
    return {
        'aria-label': ariaLabel,
        style: { fontSize: FILTER_FONT_SIZE },
    };
}
function filterFieldSx(isActive) {
    return {
        width: '100%',
        minWidth: 0,
        '& .MuiInputBase-root': {
            height: `${FILTER_TOKENS.height}px !important`,
            minHeight: `${FILTER_TOKENS.height}px !important`,
            bgcolor: isActive ? FILTER_TOKENS.bgActive : FILTER_TOKENS.bg,
            borderRadius: FILTER_TOKENS.radius,
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
        },
        '& .MuiInputBase-root.Mui-focused': {
            boxShadow: 'inset 0 0 0 1px rgba(6, 150, 215, 0.35)',
        },
        '& .MuiInputBase-input': {
            fontSize: `${FILTER_FONT_SIZE} !important`,
            padding: '4px 8px !important',
            lineHeight: 1.25,
        },
        '& .MuiSelect-root.MuiInputBase-root': {
            padding: '0 !important',
            paddingLeft: '4px !important',
        },
        '& .MuiSelect-select': {
            fontSize: `${FILTER_FONT_SIZE} !important`,
            padding: '1px 0 !important',
            paddingRight: '16px !important',
            lineHeight: 1.25,
            minHeight: 'unset !important',
        },
        '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder': {
            fontSize: `${FILTER_FONT_SIZE} !important`,
            ...placeholderColorSx,
        },
        '& input[type="date"]:invalid::-webkit-datetime-edit': placeholderColorSx,
        '& input[type="date"]:invalid::-webkit-datetime-edit-fields-wrapper': placeholderColorSx,
        '& input[type="date"]:valid::-webkit-datetime-edit, & input[type="date"]:valid::-webkit-datetime-edit-fields-wrapper': {
            color: 'inherit !important',
            WebkitTextFillColor: 'inherit !important',
        },
        '& [data-filter-placeholder]': placeholderColorSx,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isActive ? FILTER_TOKENS.borderActive : FILTER_TOKENS.border,
            legend: { display: 'none' },
        },
        '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isActive ? FILTER_TOKENS.borderActive : FILTER_TOKENS.borderHover,
        },
        '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: FILTER_TOKENS.borderActive,
            borderWidth: 1,
        },
        '& .MuiSelect-icon.MuiSelect-iconOutlined': {
            width: '14px !important',
            minWidth: '14px !important',
            padding: '0 2px 0 0 !important',
        },
        '& .MuiSelect-icon': {
            right: '0 !important',
            top: 'unset !important',
            bottom: 0,
            width: '14px !important',
            height: `${FILTER_TOKENS.height}px !important`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
        },
        '& .MuiSelect-icon > .MuiSvgIcon-root:not(.MuiSelectWeave-icon-ai)': {
            width: '12px !important',
            height: '12px !important',
            margin: '0 !important',
            fill: 'rgba(0, 0, 0, 0.54) !important',
        },
        '& .MuiSelect-icon.MuiSelect-iconOpen > .MuiSvgIcon-root:not(.MuiSelectWeave-icon-ai)': {
            fill: 'rgba(0, 0, 0, 0.72) !important',
            transform: 'rotate(180deg) !important',
        },
    };
}
function FilterPlaceholder({ children }) {
    return (_jsx(Box, { component: "span", "data-filter-placeholder": true, sx: { ...placeholderColorSx, fontSize: FILTER_FONT_SIZE, display: 'inline' }, children: children }));
}
const filterRowSx = {
    bgcolor: 'rgba(0, 0, 0, 0.025)',
    '& td': { borderBottom: 1, borderColor: 'divider' },
};
const filterCellSx = { py: 0.375, px: 0.75, verticalAlign: 'middle' };
const RESULTS_COLUMN_INDEX = JOB_TABLE_COLUMNS.findIndex((c) => c.id === 'results');
const filterBandCellSx = {
    py: 0.625,
    bgcolor: 'rgba(6, 150, 215, 0.04)',
    borderBottom: 1,
    borderColor: 'divider',
};
const tableCellSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 0,
};
const tableSx = {
    tableLayout: 'fixed',
    width: '100%',
};
const ACTIVE_CHIP_SX = {
    height: 24,
    fontSize: FILTER_FONT_SIZE,
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    '& .MuiChip-deleteIcon': { fontSize: 16 },
};
const JobStatusTab = () => {
    const queryClient = useQueryClient();
    const [cursorStack, setCursorStack] = useState([undefined]);
    const currentCursor = cursorStack[cursorStack.length - 1];
    const [textFilters, setTextFilters] = useState({ batchId: '', createdBy: '' });
    const [debouncedText, setDebouncedText] = useState({ batchId: '', createdBy: '' });
    const [taskFilter, setTaskFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [dbSnapshotDateFilter, setDbSnapshotDateFilter] = useState(null);
    const [createdAtFilter, setCreatedAtFilter] = useState(null);
    const [downloadingJobIds, setDownloadingJobIds] = useState(() => new Set());
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const inFlightJobIdsRef = useRef(new Set());
    const [downloadErrors, setDownloadErrors] = useState(() => new Map());
    const [cancellingJobIds, setCancellingJobIds] = useState(() => new Set());
    const [cancelTarget, setCancelTarget] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { data: casesData } = useQuery({
        queryKey: queryKeys.dataFix.cases(),
        queryFn: () => api.dataFix.getCases(),
    });
    const cases = casesData?.cases ?? [];
    const setJobError = (jobId, message) => {
        setDownloadErrors((prev) => {
            const next = new Map(prev);
            if (message === null)
                next.delete(jobId);
            else
                next.set(jobId, message);
            return next;
        });
    };
    const beginDownload = (jobId) => {
        if (inFlightJobIdsRef.current.has(jobId))
            return false;
        inFlightJobIdsRef.current.add(jobId);
        setDownloadingJobIds((prev) => {
            const next = new Set(prev);
            next.add(jobId);
            return next;
        });
        return true;
    };
    const endDownload = (jobId) => {
        inFlightJobIdsRef.current.delete(jobId);
        setDownloadingJobIds((prev) => {
            const next = new Set(prev);
            next.delete(jobId);
            return next;
        });
    };
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedText({
            batchId: textFilters.batchId.trim(),
            createdBy: textFilters.createdBy.trim(),
        }), 300);
        return () => clearTimeout(handle);
    }, [textFilters]);
    const activeFilters = useMemo(() => {
        const filters = {};
        if (debouncedText.batchId)
            filters.batchId = debouncedText.batchId;
        if (debouncedText.createdBy)
            filters.createdBy = debouncedText.createdBy;
        if (taskFilter)
            filters.task = taskFilter;
        if (statusFilter)
            filters.status = statusFilter;
        if (dbSnapshotDateFilter)
            filters.dbSnapshotDate = dbSnapshotDateFilter;
        if (createdAtFilter)
            filters.createdAt = createdAtFilter;
        return filters;
    }, [debouncedText, taskFilter, statusFilter, dbSnapshotDateFilter, createdAtFilter]);
    const filtersKey = JSON.stringify(activeFilters);
    const activeFilterCount = Object.keys(activeFilters).length;
    const clearAllFilters = () => {
        setTextFilters({ batchId: '', createdBy: '' });
        setDebouncedText({ batchId: '', createdBy: '' });
        setTaskFilter(null);
        setStatusFilter(null);
        setDbSnapshotDateFilter(null);
        setCreatedAtFilter(null);
    };
    useEffect(() => {
        setCursorStack([undefined]);
    }, [filtersKey]);
    const { data, isLoading, isFetching, error, refetch } = useQuery({
        queryKey: queryKeys.dataFix.jobs(currentCursor, activeFilters),
        queryFn: () => api.dataFix.getJobs(buildJobQueryParams(currentCursor, activeFilters)),
        placeholderData: keepPreviousData,
    });
    const cancelMutation = useMutation({
        mutationFn: ({ jobId, batchId }) => api.dataFix.cancelJob(jobId, batchId),
        onMutate: ({ jobId }) => {
            setCancellingJobIds((prev) => new Set(prev).add(jobId));
            return { jobId };
        },
        onSuccess: (_, { jobId }) => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.dataFix.all });
            setSnackbar({ open: true, message: `Job ${jobId} cancel requested`, severity: 'success' });
        },
        onError: (err, _vars, context) => {
            if (context) {
                setCancellingJobIds((prev) => {
                    const next = new Set(prev);
                    next.delete(context.jobId);
                    return next;
                });
            }
            const message = err.message || `Failed to cancel job ${context?.jobId ?? ''}`;
            setSnackbar({ open: true, message, severity: 'error' });
        },
    });
    const jobs = data?.jobs ?? [];
    const nextCursor = data?.nextCursor ?? null;
    const isFirstPage = cursorStack.length === 1;
    const columnWidths = useMemo(() => computeJobTableColumnWidths(jobs), [jobs]);
    const goNext = () => {
        if (nextCursor)
            setCursorStack((stack) => [...stack, nextCursor]);
    };
    const goPrev = () => {
        if (cursorStack.length > 1)
            setCursorStack((stack) => stack.slice(0, -1));
    };
    const handleDownload = async (job) => {
        if (!job.s3ResultKey)
            return;
        if (!beginDownload(job.jobId))
            return;
        const s3ResultKey = job.s3ResultKey;
        setJobError(job.jobId, null);
        try {
            const { downloadUrl } = await api.dataFix.getJobDownloadUrl({ s3ResultKey });
            triggerBrowserDownload(downloadUrl);
        }
        catch (err) {
            const apiErr = err;
            setJobError(job.jobId, apiErr.message || 'Failed to download results.');
        }
        finally {
            endDownload(job.jobId);
        }
    };
    const handleDownloadAll = async () => {
        if (isDownloadingAll)
            return;
        setIsDownloadingAll(true);
        try {
            const { downloadUrl } = await api.dataFix.downloadAll(activeFilters);
            triggerBrowserDownload(downloadUrl);
        }
        catch (err) {
            const apiErr = err;
            setSnackbar({
                open: true,
                message: apiErr.message || 'Failed to download all results.',
                severity: 'error',
            });
        }
        finally {
            setIsDownloadingAll(false);
        }
    };
    const selectedTaskLabel = taskFilter === null
        ? null
        : (cases.find((c) => c.taskName === taskFilter)?.displayName ?? taskFilter);
    return (_jsxs(Box, { sx: { mt: 2 }, children: [_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mb: 1 }, children: _jsx(Button, { variant: "outlined", size: "small", onClick: () => void refetch(), disabled: isFetching, startIcon: isFetching ? _jsx(CircularProgress, { size: 16 }) : null, children: isFetching ? 'Refreshing…' : 'Refresh' }) }), error && (_jsx(Paper, { sx: { p: 3, mb: 2 }, children: _jsxs(Typography, { color: "error", children: ["Failed to load jobs: ", error.message] }) })), !error && (_jsxs(_Fragment, { children: [_jsx(Paper, { children: _jsx(TableContainer, { children: _jsxs(Table, { size: "small", sx: tableSx, children: [_jsx("colgroup", { children: columnWidths.map((width, index) => (_jsx("col", { style: { width } }, JOB_TABLE_COLUMNS[index].id))) }), _jsxs(TableHead, { children: [_jsx(TableRow, { sx: { '& th': { fontWeight: 700 } }, children: JOB_TABLE_COLUMNS.map((col) => (_jsx(TableCell, { children: col.header }, col.id))) }), _jsxs(TableRow, { sx: filterRowSx, children: [_jsx(TableCell, { sx: filterCellSx, children: _jsx(TextField, { variant: "outlined", size: "small", placeholder: "Batch ID", value: textFilters.batchId, onChange: (e) => setTextFilters((prev) => ({ ...prev, batchId: e.target.value })), sx: {
                                                                ...filterFieldSx(!!textFilters.batchId),
                                                                width: '100%',
                                                                maxWidth: '100%',
                                                            }, inputProps: filterInputProps('Filter jobs by batch id') }) }), _jsx(TableCell, { sx: filterCellSx }), _jsx(TableCell, { sx: filterCellSx, children: _jsx(FormControl, { size: "small", fullWidth: true, sx: filterFieldSx(!!taskFilter), children: _jsxs(Select, { variant: "outlined", value: taskFilter ?? '', displayEmpty: true, renderValue: (selected) => {
                                                                    if (!selected)
                                                                        return _jsx(FilterPlaceholder, { children: "All tasks" });
                                                                    const match = cases.find((c) => c.taskName === selected);
                                                                    return match?.displayName ?? selected;
                                                                }, onChange: (e) => {
                                                                    const value = e.target.value;
                                                                    setTaskFilter(value === '' ? null : value);
                                                                }, inputProps: { 'aria-label': 'Filter jobs by task' }, children: [_jsx(MenuItem, { value: "", sx: { fontSize: FILTER_FONT_SIZE }, children: "All tasks" }), cases.map((c) => (_jsx(MenuItem, { value: c.taskName, sx: { fontSize: FILTER_FONT_SIZE }, children: c.displayName }, c.taskName)))] }) }) }), _jsx(TableCell, { sx: filterCellSx, children: _jsx(TextField, { variant: "outlined", size: "small", fullWidth: true, type: "date", value: dbSnapshotDateFilter ?? '', onChange: (e) => setDbSnapshotDateFilter(e.target.value === '' ? null : e.target.value), sx: filterFieldSx(!!dbSnapshotDateFilter), inputProps: filterInputProps('Filter jobs by DB snapshot date') }) }), _jsx(TableCell, { sx: filterCellSx, children: _jsx(FormControl, { size: "small", fullWidth: true, sx: filterFieldSx(!!statusFilter), children: _jsxs(Select, { variant: "outlined", value: statusFilter ?? '', displayEmpty: true, renderValue: (selected) => {
                                                                    if (!selected) {
                                                                        return _jsx(FilterPlaceholder, { children: "All statuses" });
                                                                    }
                                                                    const config = STATUS_CONFIG[selected];
                                                                    return (_jsx(Box, { component: "span", sx: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: config.label }));
                                                                }, onChange: (e) => {
                                                                    const value = e.target.value;
                                                                    setStatusFilter(value === '' ? null : value);
                                                                }, inputProps: { 'aria-label': 'Filter jobs by status' }, children: [_jsx(MenuItem, { value: "", sx: { fontSize: FILTER_FONT_SIZE }, children: "All statuses" }), STATUS_FILTER_OPTIONS.map((opt) => (_jsx(MenuItem, { value: opt.value, sx: { fontSize: FILTER_FONT_SIZE }, children: opt.label }, opt.value)))] }) }) }), _jsx(TableCell, { sx: filterCellSx, children: _jsx(TextField, { variant: "outlined", size: "small", placeholder: "Name", value: textFilters.createdBy, onChange: (e) => setTextFilters((prev) => ({ ...prev, createdBy: e.target.value })), sx: {
                                                                ...filterFieldSx(!!textFilters.createdBy),
                                                                width: '100%',
                                                                maxWidth: '100%',
                                                            }, inputProps: filterInputProps('Filter jobs by created by') }) }), _jsx(TableCell, { sx: filterCellSx, children: _jsx(TextField, { variant: "outlined", size: "small", fullWidth: true, type: "date", value: createdAtFilter ?? '', onChange: (e) => setCreatedAtFilter(e.target.value === '' ? null : e.target.value), sx: filterFieldSx(!!createdAtFilter), inputProps: filterInputProps('Filter jobs by created at date') }) }), _jsx(TableCell, { sx: filterCellSx }), _jsx(TableCell, { sx: filterCellSx }), _jsx(TableCell, { sx: filterCellSx })] }), activeFilterCount > 0 && (_jsxs(TableRow, { children: [_jsx(TableCell, { colSpan: RESULTS_COLUMN_INDEX, sx: filterBandCellSx, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.75,
                                                                flexWrap: 'wrap',
                                                            }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mr: 0.25 }, children: "Filtered by" }), debouncedText.batchId && (_jsx(Chip, { label: `Batch: ${debouncedText.batchId}`, size: "small", variant: "outlined", onDelete: () => setTextFilters((prev) => ({ ...prev, batchId: '' })), sx: ACTIVE_CHIP_SX })), debouncedText.createdBy && (_jsx(Chip, { label: `Created by: ${debouncedText.createdBy}`, size: "small", variant: "outlined", onDelete: () => setTextFilters((prev) => ({ ...prev, createdBy: '' })), sx: ACTIVE_CHIP_SX })), taskFilter && selectedTaskLabel && (_jsx(Chip, { label: `Task: ${selectedTaskLabel}`, size: "small", variant: "outlined", onDelete: () => setTaskFilter(null), sx: ACTIVE_CHIP_SX })), statusFilter && (_jsx(Chip, { label: STATUS_CONFIG[statusFilter].label, size: "small", variant: "outlined", onDelete: () => setStatusFilter(null), sx: ACTIVE_CHIP_SX })), dbSnapshotDateFilter && (_jsx(Chip, { label: `Snapshot: ${dbSnapshotDateFilter}`, size: "small", variant: "outlined", onDelete: () => setDbSnapshotDateFilter(null), sx: ACTIVE_CHIP_SX })), createdAtFilter && (_jsx(Chip, { label: `Created: ${createdAtFilter}`, size: "small", variant: "outlined", onDelete: () => setCreatedAtFilter(null), sx: ACTIVE_CHIP_SX }))] }) }), _jsx(TableCell, { sx: filterBandCellSx, children: _jsx(Button, { size: "small", onClick: () => void handleDownloadAll(), disabled: isDownloadingAll, startIcon: isDownloadingAll ? _jsx(CircularProgress, { size: 16 }) : null, children: isDownloadingAll ? 'Preparing…' : 'Download All' }) }), _jsx(TableCell, { colSpan: JOB_TABLE_COLUMNS.length - RESULTS_COLUMN_INDEX - 1, sx: filterBandCellSx, children: _jsx(Button, { size: "small", onClick: clearAllFilters, children: "Clear all" }) })] }))] }), _jsxs(TableBody, { children: [isLoading && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: JOB_TABLE_COLUMNS.length, align: "center", sx: { py: 4 }, children: _jsx(CircularProgress, { size: 24 }) }) })), !isLoading && jobs.length === 0 && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: JOB_TABLE_COLUMNS.length, align: "center", children: _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { py: 3 }, children: "No data fix jobs found." }) }) })), !isLoading &&
                                                jobs.map((job) => {
                                                    const statusConfig = STATUS_CONFIG[job.status] ?? {
                                                        label: job.status,
                                                        color: 'text.secondary',
                                                    };
                                                    const isDownloading = downloadingJobIds.has(job.jobId);
                                                    return (_jsxs(TableRow, { hover: true, children: [_jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", fontFamily: "monospace", noWrap: true, title: job.batchId, children: job.batchId }) }), _jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", fontFamily: "monospace", noWrap: true, title: job.jobId, children: job.jobId }) }), _jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", noWrap: true, title: job.caseName || '—', children: job.caseName || '—' }) }), _jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", noWrap: true, title: job.dbSnapshotDate, children: job.dbSnapshotDate }) }), _jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", noWrap: true, title: statusConfig.label, sx: { color: statusConfig.color, fontWeight: 500 }, children: statusConfig.label }) }), _jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", noWrap: true, title: job.createdBy, children: job.createdBy }) }), _jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", noWrap: true, title: formatDateTime(job.createdAt), children: formatDateTime(job.createdAt) }) }), _jsx(TableCell, { sx: tableCellSx, children: _jsx(Typography, { variant: "body2", noWrap: true, title: String(job.collectionCount), children: job.collectionCount }) }), _jsx(TableCell, { children: job.status !== 'scheduled' &&
                                                                    job.status !== 'inprogress' &&
                                                                    job.status !== 'cancelling' &&
                                                                    (job.s3ResultKey ? (_jsxs(Box, { children: [_jsx(Link, { component: "button", variant: "body", onClick: () => void handleDownload(job), "aria-disabled": isDownloading, "aria-busy": isDownloading, sx: {
                                                                                    opacity: isDownloading ? 0.5 : 1,
                                                                                    cursor: isDownloading ? 'default' : 'pointer',
                                                                                    pointerEvents: isDownloading ? 'none' : 'auto',
                                                                                }, children: isDownloading ? 'Preparing…' : 'Download Results' }), downloadErrors.has(job.jobId) && (_jsx(Typography, { variant: "body2", color: "error", sx: { mt: 0.5 }, role: "alert", children: downloadErrors.get(job.jobId) }))] })) : (_jsx(Tooltip, { title: "Result file is not available for this job. Missing s3ResultKey.", children: _jsxs(Box, { sx: {
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                color: 'text.secondary',
                                                                            }, children: [_jsx(InfoOutlinedIcon, { fontSize: "small" }), _jsx(Typography, { variant: "body2", sx: { ml: 0.5 }, children: "Unavailable" })] }) }))) }), _jsx(TableCell, { children: job.status === 'inprogress' && (_jsx(Button, { size: "small", color: "error", variant: "outlined", disabled: cancellingJobIds.has(job.jobId), onClick: () => setCancelTarget(job), children: "Cancel" })) })] }, job.jobId));
                                                })] })] }) }) }), !isLoading && (jobs.length > 0 || !isFirstPage) && (_jsxs(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 1,
                            gap: 1,
                        }, children: [_jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["Showing ", jobs.length, " ", jobs.length === 1 ? 'job' : 'jobs'] }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: goPrev, disabled: isFirstPage || isFetching, children: "Prev" }), _jsx(Button, { variant: "outlined", size: "small", onClick: goNext, disabled: !nextCursor || isFetching, children: "Next" })] })] }))] })), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 4000, onClose: () => setSnackbar((s) => ({ ...s, open: false })), children: _jsx(Alert, { severity: snackbar.severity, onClose: () => setSnackbar((s) => ({ ...s, open: false })), children: snackbar.message }) }), _jsx(Dialog, { open: cancelTarget !== null, onClose: () => setCancelTarget(null), maxWidth: "sm", children: cancelTarget && (_jsxs(_Fragment, { children: [_jsxs(DialogTitle, { children: ["Cancel job for batch ", cancelTarget.batchId, "?"] }), _jsx(DialogContent, { children: _jsxs(DialogContentText, { children: ["Workers will stop processing job ", cancelTarget.jobId, "."] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setCancelTarget(null), children: "Keep job" }), _jsx(Button, { color: "error", variant: "contained", onClick: () => {
                                        cancelMutation.mutate({
                                            jobId: cancelTarget.jobId,
                                            batchId: cancelTarget.batchId,
                                        });
                                        setCancelTarget(null);
                                    }, children: "Cancel job" })] })] })) })] }));
};
export default JobStatusTab;
